import { Grid, Stack, SwipeableDrawer, Typography, useMediaQuery, useTheme } from "@mui/material";
import Container from "@mui/material/Container";
import * as React from "react";
import { ActivityEnum, useUserActivityTracker } from "../UserActivityTracker";
import CustomSentenceInterface from "./CustomSentenceInterface";
import CustomSentenceMenu from "./CustomSentenceMenu";
import AppBarContext, { AppBarMenuIndex } from "../context/AppBarContext";
import CustomSentenceListContext, { CustomSentenceListProvider } from "../context/CustomSentenceListContext";
import { BORDER } from "../style/verbaScholaPalette";

/** Shared sentence-list and pronunciation workflow for legacy and redesigned routes. */
export default function CustomSentenceWorkspace({ alwaysShowPinyin = false, compactListDetail = false, redesigned = false }: { alwaysShowPinyin?: boolean; compactListDetail?: boolean; redesigned?: boolean }) {
    // so I have access to context, and auto-collapse the sidebar
    return (
        <CustomSentenceListProvider>
            <CustomSentenceWithProvider alwaysShowPinyin={alwaysShowPinyin} compactListDetail={compactListDetail} redesigned={redesigned} />
        </CustomSentenceListProvider>
    );
}

function CustomSentenceWithProvider({ alwaysShowPinyin, compactListDetail, redesigned }: { alwaysShowPinyin: boolean; compactListDetail: boolean; redesigned: boolean }) {

    useUserActivityTracker(ActivityEnum.CustomSentence)

    const theme = useTheme();
    // Check if screen is medium or smaller
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const compact = compactListDetail && isMobile;
    const isTouchDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && window.matchMedia("(pointer: coarse)").matches;
    const inlineMenu = redesigned || !isTouchDevice;

    const { setMenuIndex } = React.useContext(AppBarContext);

    const { selectedSentence } = React.useContext(CustomSentenceListContext);

    const [isMenuCollapsed, setIsMenuCollapsed] = React.useState(false);

    React.useEffect(() => {
        setMenuIndex(AppBarMenuIndex.CustomSentence);
        if (isMenuCollapsed && selectedSentence.id < 0 && selectedSentence.text !== 'AddSentence') {
            setIsMenuCollapsed(false)
        }
    }, [isMenuCollapsed, selectedSentence.id, selectedSentence.text, setMenuIndex]);

    // // Auto-collapse sidebar on mobile screens
    // React.useEffect(() => {
    //     if (isMobile || isTouchDevice) {
    //         setIsMenuCollapsed(selectedSentence.id > 0)
    //     } else {
    //         setIsMenuCollapsed(false);
    //     }
    // }, [isMobile, selectedSentence]);

    return (
        <Grid
            style={{
                display: "flex",
                width: "100%",
                height: "100%",
                position: "relative",
                overflow: "hidden",
                gap: redesigned ? 20 : 0,
            }}>
            {/* Menu */}
            {!inlineMenu ? (
                <SwipeableDrawer
                    anchor="left"
                    open={!isMenuCollapsed}
                    onClose={() => setIsMenuCollapsed(true)}
                    onOpen={() => setIsMenuCollapsed(false)}
                    disableDiscovery={false}
                    disableSwipeToOpen={false}
                    PaperProps={{
                        sx: { backgroundColor: redesigned ? '#fff' : "#002A6E" },
                    }}
                >
                    <Container
                        disableGutters={redesigned}
                        sx={{
                            opacity: isMenuCollapsed ? 0 : 1,
                            transition: reduceMotion ? 'none' : "opacity 0.3s ease-in-out",
                            px: redesigned ? 0 : undefined,
                        }}>
                        <CustomSentenceMenu isMenuCollapsed={isMenuCollapsed} onToggleMenuCollapse={() => setIsMenuCollapsed(!isMenuCollapsed)} isMobile={isMobile} isTouchDevice={isTouchDevice} redesigned={redesigned} />
                    </Container>
                    {/* swipe hint footer */}
                    <Stack direction="column" sx={{ width: '100%', color: "#FFFFFFBB", userSelect: "none", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                        <Typography>Swipe left to close</Typography>
                        <Typography>Swipe right to open</Typography>
                    </Stack>
                </SwipeableDrawer>

            ) : (
                <Grid
                    data-testid={redesigned ? 'pronunciation-sentence-list' : undefined}
                    style={{
                        width: isMenuCollapsed ? "0" : compact ? "100%" : redesigned ? "clamp(290px, 27%, 330px)" : "33.33%",
                        minWidth: isMenuCollapsed ? "0" : compact ? "100%" : redesigned ? "290px" : "300px",
                        maxWidth: isMenuCollapsed ? "0" : compact ? "100%" : redesigned ? "330px" : "33.33%",
                        height: "100%",
                        transition: reduceMotion ? 'none' : "all 0.3s ease-in-out",
                        overflow: "hidden",
                        backgroundColor: redesigned ? '#fff' : "#002A6E", // Blue background for legacy sidebar
                        border: redesigned ? `1px solid ${BORDER}` : undefined,
                        borderRadius: redesigned ? 20 : undefined,
                        flexShrink: 0,
                    }}>
                    <Container
                        disableGutters={redesigned}
                        sx={{
                            height: "100%",
                            opacity: isMenuCollapsed ? 0 : 1,
                            transition: reduceMotion ? 'none' : "opacity 0.3s ease-in-out",
                            px: redesigned ? 0 : undefined,
                        }}>
                        <CustomSentenceMenu isMenuCollapsed={isMenuCollapsed} onToggleMenuCollapse={() => setIsMenuCollapsed(!isMenuCollapsed)} isMobile={isMobile} isTouchDevice={isTouchDevice} redesigned={redesigned} />
                    </Container>
                </Grid>


            )}

            {/* Content */}
            <Grid
                data-testid={redesigned ? 'pronunciation-practice-card' : undefined}
                style={{
                    width: isMenuCollapsed ? "100%" : compact && inlineMenu ? "0" : redesigned ? 'auto' : "66.67%",
                    transition: reduceMotion ? 'none' : "all 0.3s ease-in-out",
                    flexGrow: compact && inlineMenu && !isMenuCollapsed ? 0 : 1,
                    minWidth: 0,
                    height: "100%",
                    overflow: "hidden",
                    backgroundColor: redesigned ? '#fff' : "#F2F7FF",
                    border: redesigned ? `1px solid ${BORDER}` : undefined,
                    borderRadius: redesigned ? 20 : undefined,
                }}>
                <CustomSentenceInterface isMenuCollapsed={isMenuCollapsed} onToggleMenuCollapse={() => setIsMenuCollapsed(!isMenuCollapsed)} isMobile={isMobile} alwaysShowPinyin={alwaysShowPinyin} redesigned={redesigned} />
            </Grid>
        </Grid>
    );
}
